import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import TransactionService from "../../../services/api/transaction.service";
import Withdrawal from "../../features/Wallet/Withdrawal";
import TransactionHistory from "./TransactionList";
// import OrderHistory from "./OrderHistory";
import { useNavigation } from "@/contexts/notification/NavigationContext";

const Balance = ({
  walletData,
  error: parentError,
  onWalletUpdate,
  isRestricted,
  kycStatus,
  onCompleteKyc,
  onClose = () => {},
}) => {
  // Add navigation hook for programmatic navigation
  const navigate = useNavigate();

  // Local UI state only
  const [isInitiatingDeposit, setIsInitiatingDeposit] = useState(false);
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);
  const [depositAmount, setDepositAmount] = useState("");
  const [depositSuccess, setDepositSuccess] = useState(false);
  const [paymentResponse, setPaymentResponse] = useState(null);
  const [verificationMessage, setVerificationMessage] = useState("");
  const [activeTab, setActiveTab] = useState("deposit");
  const [paymentCancelled, setPaymentCancelled] = useState(false);
  const [cancellationMessage, setCancellationMessage] = useState("");
  const [localError, setLocalError] = useState(null);
  const [inputError, setInputError] = useState(null);
  const [currentOrderId, setCurrentOrderId] = useState(null); // Track current order ID
  const [isCancellingPayment, setIsCancellingPayment] = useState(false); // Track cancellation process
  const [razorpayInstance, setRazorpayInstance] = useState(null); // Store Razorpay instance
  const {
    pendingTransaction,
    clearPendingPurchase,
    hasSufficientFundsForPending,
    getReturnDestination,
  } = useNavigation();

  const razorpayRef = useRef(null); // Add this ref for consistent access
  const [showReturnToPurchase, setShowReturnToPurchase] = useState(false);
  const [addFundsSuccess, setAddFundsSuccess] = useState("");

  // Auto-hide success message after 5 seconds
  useEffect(() => {
    let timer;
    if (depositSuccess) {
      timer = setTimeout(() => {
        setDepositSuccess(false);
        setVerificationMessage("");
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [depositSuccess]);

  // Auto-hide input error after 3 seconds
  useEffect(() => {
    let timer;
    if (inputError) {
      timer = setTimeout(() => {
        setInputError(null);
      }, 3000);
    }
    return () => clearTimeout(timer);
  }, [inputError]);

  // Effect to handle payment verification when payment response changes
  useEffect(() => {
    if (paymentResponse) {
      handlePaymentVerification();
    }
  }, [paymentResponse]);

  // Effect to show return button when funds are added successfully
  useEffect(() => {
    if (
      pendingTransaction &&
      hasSufficientFundsForPending(walletData?.balance)
    ) {
      setShowReturnToPurchase(true);
    } else {
      setShowReturnToPurchase(false);
    }
  }, [pendingTransaction, walletData?.balance, hasSufficientFundsForPending]);

  // Cleanup Razorpay instance when component unmounts
  useEffect(() => {
    return () => {
      // Close Razorpay modal if it exists when component unmounts
      if (razorpayInstance) {
        try {
          razorpayInstance.close();
        } catch (err) {
          console.error("Error closing Razorpay instance:", err);
        }
      }
    };
  }, [razorpayInstance]);

  const handleAddFundsSuccess = (amount) => {
    // Show success message
    setAddFundsSuccess(
      `Successfully added ₹${amount.toLocaleString("en-IN")} to your wallet`
    );

    // If there's a pending transaction and funds are now sufficient, show return button
    if (
      pendingTransaction &&
      hasSufficientFundsForPending(walletData?.balance)
    ) {
      setShowReturnToPurchase(true);
    }

    // Refresh wallet data
    if (onWalletUpdate) onWalletUpdate();
  };

  const handlePaymentVerification = async () => {
    if (!paymentResponse) return;

    setIsVerifyingPayment(true);
    try {
      const verificationData = {
        razorpay_payment_id: paymentResponse.razorpay_payment_id,
        razorpay_order_id: paymentResponse.razorpay_order_id,
        razorpay_signature: paymentResponse.razorpay_signature,
        amount: Number(depositAmount),
      };

      const response = await TransactionService.verifyPayment(verificationData);

      if (response.success) {
        setDepositSuccess(true);
        setVerificationMessage(
          "Payment verified successfully! Your balance has been updated."
        );
        // Call our new success handler with the deposit amount
        handleAddFundsSuccess(Number(depositAmount));
      } else {
        setLocalError(response.error || "Payment verification failed");
        setVerificationMessage(
          "Payment verification failed. Please contact support."
        );
      }
    } catch (err) {
      setLocalError("Failed to verify payment. Please try again later.");
      setVerificationMessage("Error occurred during payment verification.");
    } finally {
      setIsVerifyingPayment(false);
      setPaymentResponse(null);
      setDepositAmount("");
      setIsInitiatingDeposit(false);
      setCurrentOrderId(null); // Clear the order ID after verification
      setRazorpayInstance(null); // Clear the Razorpay instance
    }
  };

  // Enhanced function to handle technical errors

  const handleTechnicalError = (orderId, errorMessage) => {

    // Close Razorpay modal if instance exists
    if (razorpayInstance) {
      try {
        razorpayInstance.close();
      } catch (err) {
        console.error(
          "Error closing Razorpay instance on technical error:",
          err
        );
      }
      // Clear the instance immediately
      setRazorpayInstance(null);
    }

    // Cancel the payment through API
    handlePaymentCancellation(orderId);

    // Show error to user
    setLocalError(
      `Payment failed: ${errorMessage || "Technical error occurred"}`
    );
  };

  // Improved openRazorpayCheckout function
  const openRazorpayCheckout = (orderData) => {
    if (!window.Razorpay) {
      setLocalError("Payment gateway not available. Please try again later.");
      setIsInitiatingDeposit(false);
      return;
    }

    if (!orderData.razorpayOrderId) {
      setLocalError("Missing order information. Please try again.");
      setIsInitiatingDeposit(false);
      return;
    }

    // Store the current order ID for potential cancellation
    setCurrentOrderId(orderData.razorpayOrderId);

    const options = {
      key: orderData.razorpayKeyId || "rzp_test_TSoYpM7KDKetuw",
      amount: orderData.amount || Number(depositAmount) * 100,
      currency: orderData.currency || "INR",
      name: orderData.name || "Your App Name",
      description: orderData.description || "Wallet Deposit",
      order_id: orderData.razorpayOrderId,
      handler: function (response) {
        // Clear the instance immediately upon successful payment
        razorpayRef.current = null;
        setRazorpayInstance(null);
        setPaymentResponse(response);
      },
      modal: {
        ondismiss: function () {
          // Clear the instance immediately when modal is dismissed
          razorpayRef.current = null;
          setRazorpayInstance(null);
          // Call the API cancellation handler when modal is dismissed
          handlePaymentCancellation(orderData.razorpayOrderId);
        },
        escape: false, // Prevent closing on ESC key
        backdropclose: false, // Prevent closing on backdrop click
      },
      prefill: {
        name: walletData?.user?.name || "",
        email: walletData?.user?.email || "",
        contact: walletData?.user?.phone || "",
      },
      notes: {
        address: "User Wallet Deposit",
      },
      theme: {
        color: "#3399cc",
      },
    };

    try {
      const razorpay = new window.Razorpay(options);

      // Keep both the state and ref synchronized
      setRazorpayInstance(razorpay);
      razorpayRef.current = razorpay;

      // Define a closure function to close the modal and handle the error
      const handlePaymentIssue = (errorMessage) => {
        // Close the Razorpay modal first if it exists
        try {
          if (razorpayRef.current) {
            razorpayRef.current.close();
            razorpayRef.current = null;
            setRazorpayInstance(null);
          }
        } catch (err) {
          console.error("Error closing Razorpay modal:", err);
        }

        // Then handle the technical error
        handleTechnicalError(
          orderData.razorpayOrderId,
          errorMessage || "Payment failed"
        );
      };

      // Enhanced error handling with force-closing of failure modals
      razorpay.on("payment.failed", function (response) {
       

        // Force close any open modal
        try {
          // First close the main Razorpay instance
          razorpay.close();

          // Clear references immediately
          razorpayRef.current = null;
          setRazorpayInstance(null);

          // Force remove any remaining modal elements that might be stuck
          const razorpayModals = document.querySelectorAll(
            ".razorpay-container, .razorpay-backdrop, .razorpay-checkout-frame"
          );
          razorpayModals.forEach((modal) => {
            if (modal && modal.parentNode) {
              modal.parentNode.removeChild(modal);
            }
          });

          // Also try to remove any error modals specifically
          const errorModals = document.querySelectorAll(
            '[data-testid="rzp-error-modal"], .razorpay-payment-form'
          );
          errorModals.forEach((modal) => {
            if (modal && modal.parentNode) {
              modal.parentNode.removeChild(modal);
            }
          });
        } catch (err) {
          console.error("Error forcibly closing Razorpay failure modal:", err);
        }

        // Handle the error with server notification
        const errorData = response.error || {};
        handleTechnicalError(
          orderData.razorpayOrderId,
          errorData.description || errorData.reason || "Payment failed"
        );

        // Add a small delay and then force a navigation
        setTimeout(() => {
          navigate("/wallet");
        }, 500);
      });

      // Also handle the error event similarly
      razorpay.on("error", function (error) {
     

        try {
          // Force close Razorpay instance
          razorpay.close();

          // Clear references
          razorpayRef.current = null;
          setRazorpayInstance(null);

          // Force remove modal elements
          const razorpayElements = document.querySelectorAll(
            '.razorpay-container, .razorpay-backdrop, .razorpay-checkout-frame, [data-testid="rzp-error-modal"]'
          );
          razorpayElements.forEach((el) => {
            if (el && el.parentNode) {
              el.parentNode.removeChild(el);
            }
          });
        } catch (err) {
          console.error("Error forcibly closing Razorpay error modal:", err);
        }

        // Force navigation after error
        setTimeout(() => {
          navigate("/wallet");
        }, 500);
      });

      razorpay.on("network.failed", function (error) {
        handlePaymentIssue("Network connection failed");
      });

      razorpay.on("error", function (error) {
        handlePaymentIssue(error?.message || "Unknown error");
      });

      // Add a timeout to automatically close if stuck open for too long (30 seconds)
      const autoCloseTimeout = setTimeout(() => {
        if (razorpayRef.current) {
       
          try {
            razorpayRef.current.close();
            razorpayRef.current = null;
            setRazorpayInstance(null);
            handlePaymentCancellation(orderData.razorpayOrderId);
          } catch (err) {
            console.error("Error in auto-close timeout:", err);
          }
        }
      }, 30000); // 30 seconds timeout

      // Open the checkout modal
      razorpay.open();

      // Clear timeout if component unmounts
      return () => clearTimeout(autoCloseTimeout);
    } catch (error) {
      setLocalError("Failed to open payment gateway. Please try again.");
      setIsInitiatingDeposit(false);
      setCurrentOrderId(null);
    }
  };

  // Improved handlePaymentCancellation in Balance.jsx
  const handlePaymentCancellation = async (
    orderId,
    reason = "User cancelled"
  ) => {
    if (!orderId) return;

    // Prevent multiple cancellation attempts
    if (isCancellingPayment) return;

    // Clean Razorpay modal first (extracted to a function)
    cleanupRazorpayModal();

    setIsCancellingPayment(true);

    try {
      const response = await TransactionService.cancelPayment({
        razorpayOrderId: orderId,
        reason: reason,
      });

      // Show cancellation UI even if backend fails
      setPaymentCancelled(true);

      if (response.success || response.uiSuccess) {
        setCancellationMessage(
          "Payment process was cancelled. Your wallet has not been charged."
        );
      } else {
        // Still show cancellation to user even if API fails
        setCancellationMessage(
          "Payment was cancelled. If you see any charges, please contact support."
        );
      }
    } catch (err) {
      setPaymentCancelled(true);
      setCancellationMessage(
        "Payment process was cancelled. Your wallet has not been charged."
      );
    } finally {
      setIsCancellingPayment(false);
      setIsInitiatingDeposit(false);
      setCurrentOrderId(null);
    }
  };

  // Extract common cleanup logic to a reusable function
  const cleanupRazorpayModal = () => {
    try {
      // Try both state and ref to ensure cleanup
      if (razorpayInstance) {
        razorpayInstance.close();
        setRazorpayInstance(null);
      }

      if (razorpayRef.current) {
        razorpayRef.current.close();
        razorpayRef.current = null;
      }

      // Force remove any stuck modal DOM elements
      const razorpayElements = document.querySelectorAll(
        '.razorpay-container, .razorpay-backdrop, .razorpay-checkout-frame, [data-testid="rzp-error-modal"]'
      );

      razorpayElements.forEach((el) => {
        if (el && el.parentNode) {
          el.parentNode.removeChild(el);
        }
      });
    } catch (err) {
      console.error("Error cleaning up Razorpay modal:", err);
      // Swallow errors in cleanup to avoid blocking cancellation
    }
  };

  // Enhanced cleanup for component unmount
  useEffect(() => {
    return () => {
      // Close Razorpay modal if it exists when component unmounts
      try {
        if (razorpayInstance) {
          razorpayInstance.close();
        }
        if (razorpayRef.current) {
          razorpayRef.current.close();
        }
      } catch (err) {
        console.error("Error closing Razorpay instance on unmount:", err);
      }
      // Clear references
      setRazorpayInstance(null);
      razorpayRef.current = null;
    };
  }, [razorpayInstance]);

  const validateAmount = (amount) => {
    if (!amount || amount.trim() === "") {
      return "Please enter a deposit amount";
    }

    const numAmount = Number(amount);
    if (isNaN(numAmount)) {
      return "Please enter a valid number";
    }

    if (numAmount <= 0) {
      return "Amount must be greater than zero";
    }

    // Add minimum deposit check if required
    if (numAmount < 100) {
      return "Minimum deposit amount is ₹100";
    }

    if (numAmount > 500000) {
      return "Maximum deposit amount is ₹5,00,000";
    }

    return null;
  };

  const handleInitiateDeposit = async () => {
    // Clear existing errors
    setLocalError(null);
    setInputError(null);

    // Validate input
    const validationError = validateAmount(depositAmount);
    if (validationError) {
      setInputError(validationError);
      return;
    }

    if (!walletData || walletData.status !== "ACTIVE") {
      setLocalError("Wallet is not active. Please contact support.");
      return;
    }

    setIsInitiatingDeposit(true);
    try {
      const response = await TransactionService.initiateDeposit(
        Number(depositAmount)
      );

      if (response.success && response.data) {
        openRazorpayCheckout(response.data);
      } else {
        setLocalError(response.error || "Failed to initiate deposit");
        setIsInitiatingDeposit(false);
      }
    } catch (err) {
      setLocalError("Failed to initiate deposit. Please try again later.");
      setIsInitiatingDeposit(false);
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    setDepositAmount(value);

    // Clear errors when user starts typing
    setInputError(null);
    setLocalError(null);
  };

  // Helper function to get the return button text based on transaction type
  const getReturnButtonText = () => {
    if (!pendingTransaction) return "Return to Complete Your Transaction";

    switch (pendingTransaction.type) {
      case "PRODUCT_PURCHASE":
        return "Return to Complete Your Purchase";
      case "MUTUAL_FUND_INVESTMENT":
        return "Return to Complete Your Investment";
      default:
        return "Return to Complete Your Transaction";
    }
  };

  // Helper function to handle return navigation
  const handleReturnNavigation = () => {
    if (!pendingTransaction) {
      navigate(getReturnDestination());
      return;
    }

    // Handle different transaction types
    switch (pendingTransaction.type) {
      case "PRODUCT_PURCHASE":
        navigate(`/company_info/${pendingTransaction.productId}`, {
          state: { fromWallet: true },
        });
        break;
      case "MUTUAL_FUND_INVESTMENT":
        navigate(`/mutual-funds/${pendingTransaction.schemeCode}`, {
          state: {
            fromWallet: true,
            investAmount: pendingTransaction.amount,
          },
        });
        break;
      default:
        navigate(getReturnDestination());
    }

    // Clear the pending purchase after navigation
    clearPendingPurchase();
  };

  // Use the parent error if provided, otherwise show local error
  const displayError = parentError || localError;

  const renderKycRestrictedDepositMessage = () => {
    if (!isRestricted || !kycStatus) return null;

    let message = "";
    let buttonText = "Complete KYC Verification";

    if (
      kycStatus.kycStatus === "pending" &&
      kycStatus.completionPercentage === 100
    ) {
      message =
        "Your KYC verification is under review. Deposit functionality will be enabled once your verification is approved.";
      buttonText = "View KYC Status";
    } else if (kycStatus.kycStatus === "pending") {
      message = "Please complete your KYC verification to enable deposits.";
    } else if (kycStatus.kycStatus === "rejected") {
      message = `Your KYC was rejected. Reason: ${
        kycStatus.rejectionReason || "Unknown reason"
      }. Please update your KYC details to enable deposits.`;
      buttonText = "Update KYC Details";
    } else {
      message =
        "KYC verification is required to deposit funds. Please complete your verification.";
    }

    return (
      <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-5 mb-4">
        <div className="flex items-start">
          <svg
            className="w-6 h-6 text-yellow-500 mr-3 mt-0.5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <div>
            <h4 className="font-semibold text-yellow-800 text-lg mb-1">
              KYC Verification Required
            </h4>
            <p className="text-yellow-700 mb-3">{message}</p>
            <button
              onClick={onCompleteKyc}
              className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded transition-colors"
            >
              {buttonText}
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <h2 className="text-2xl font-semibold mb-4">Wallet Information</h2>

      {/* Return button for any pending transaction when funds are sufficient */}
      {showReturnToPurchase && pendingTransaction && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center mb-2">
            <svg
              className="w-5 h-5 text-green-500 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <p className="text-green-700 font-medium">
              {pendingTransaction.type === "PRODUCT_PURCHASE"
                ? "You have added sufficient funds to complete your purchase!"
                : "You have added sufficient funds to complete your investment!"}
            </p>
          </div>
          <button
            onClick={handleReturnNavigation}
            className="w-full py-2 bg-green-600 hover:bg-green-700 text-white rounded transition-colors flex items-center justify-center"
          >
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 19l-7-7m0 0l7-7m-7 7h18"
              />
            </svg>
            {getReturnButtonText()}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <div className="bg-gray-50 p-4 rounded">
          <p className="text-gray-600 text-sm">Balance</p>
          <p className="text-2xl font-bold">
            {walletData?.balance || 0} {walletData?.currency || "INR"}
          </p>
        </div>
        <div className="bg-gray-50 p-4 rounded">
          <p className="text-gray-600 text-sm">Status</p>
          <div className="flex items-center">
            <span
              className={`inline-block w-3 h-3 rounded-full mr-2 ${
                walletData?.status === "ACTIVE"
                  ? "bg-green-500"
                  : walletData?.status === "FROZEN"
                  ? "bg-blue-500"
                  : "bg-red-500"
              }`}
            ></span>
            <p
              className={`font-semibold ${
                walletData?.status === "ACTIVE"
                  ? "text-green-600"
                  : walletData?.status === "FROZEN"
                  ? "text-blue-600"
                  : "text-red-600"
              }`}
            >
              {walletData?.status || "UNKNOWN"}
            </p>
          </div>
        </div>
      </div>

      <div className="mb-4 border-b border-gray-200">
        <ul className="flex flex-wrap -mb-px">
          <li className="mr-2">
            <button
              onClick={() => setActiveTab("deposit")}
              className={`inline-block py-2 px-4 font-medium ${
                activeTab === "deposit"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Deposit
            </button>
          </li>
          <li className="mr-2">
            <button
              onClick={() => setActiveTab("withdraw")}
              className={`inline-block py-2 px-4 font-medium ${
                activeTab === "withdraw"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Withdraw
            </button>
          </li>
          <li className="mr-2">
            <button
              onClick={() => setActiveTab("history")}
              className={`inline-block py-2 px-4 font-medium ${
                activeTab === "history"
                  ? "text-blue-600 border-b-2 border-blue-600"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              History
            </button>
          </li>
        </ul>
      </div>

      {activeTab === "deposit" && (
        <div className="mt-4 bg-blue-50 p-4 rounded-lg border border-blue-200">
          <h3 className="text-xl font-semibold mb-3 text-blue-800">
            Deposit Funds
          </h3>
          <p className="text-blue-700 mb-3">
            Add money to your wallet instantly.
          </p>

          {isRestricted ? (
            renderKycRestrictedDepositMessage()
          ) : (
            <>
              {depositSuccess && (
                <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4">
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 mr-2 text-green-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    {verificationMessage ||
                      "Deposit successful! Your balance will update after verification."}
                  </div>
                </div>
              )}

              {paymentCancelled && (
                <div className="bg-yellow-100 border border-yellow-300 text-yellow-700 px-4 py-3 rounded mb-4">
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 mr-2 text-yellow-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                    {cancellationMessage ||
                      "Payment failed. Redirecting to transaction dashboard..."}
                  </div>
                </div>
              )}

              {(isVerifyingPayment || isCancellingPayment) && (
                <div className="bg-yellow-100 border border-yellow-300 text-yellow-700 px-4 py-3 rounded mb-4 flex items-center">
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-yellow-700"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  {isVerifyingPayment
                    ? "Verifying your payment..."
                    : "Processing payment status..."}
                </div>
              )}

              {localError && (
                <div className="bg-red-100 border border-red-300 text-red-700 px-4 py-3 rounded mb-4">
                  <div className="flex items-center">
                    <svg
                      className="w-5 h-5 mr-2 text-red-600"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {localError}
                  </div>
                </div>
              )}

              <div className="flex flex-col md:flex-row gap-3">
                <div className="flex-grow max-w-xs">
                  <div className={`relative ${inputError ? "mb-6" : "mb-0"}`}>
                    <input
                      type="number"
                      value={depositAmount}
                      onChange={handleAmountChange}
                      placeholder="Enter amount"
                      className={`border ${
                        inputError ? "border-red-500" : "border-gray-300"
                      } rounded px-4 py-2 w-full focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      disabled={
                        isInitiatingDeposit ||
                        isVerifyingPayment ||
                        isCancellingPayment
                      }
                    />
                    {inputError && (
                      <p className="absolute text-red-500 text-xs mt-1">
                        {inputError}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={handleInitiateDeposit}
                  disabled={
                    isInitiatingDeposit ||
                    isVerifyingPayment ||
                    isCancellingPayment
                  }
                  className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:bg-blue-300 flex items-center justify-center transition-colors"
                >
                  {isInitiatingDeposit ||
                  isVerifyingPayment ||
                  isCancellingPayment ? (
                    <>
                      <svg
                        className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Processing...
                    </>
                  ) : (
                    "Deposit Now"
                  )}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {activeTab === "withdraw" && (
        <div className="mt-4">
          <Withdrawal
            onSuccess={onWalletUpdate}
            walletData={walletData}
            isRestricted={isRestricted}
            kycStatus={kycStatus}
            onCompleteKyc={onCompleteKyc}
          />
        </div>
      )}

      {activeTab === "history" && (
        <div className="mt-4">
          <TransactionHistory />
        </div>
      )}
    </div>
  );
};

export default Balance;
